import React, { useEffect, useMemo, useState } from "react";
import StyledModal from "../../components/CustomModal";
import { AddAnotherConditionalStyledButton, AddFieldWrapper, FooterButtons, StyledCollapse, StyledConditionalContainer, StyledConditionalRemoveButton, StyledTypography } from "./styles";
import { Form, FormInstance, Input, Select, Checkbox } from "antd";
import { MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { useAppSelector } from "../../utils/hooks/redux";
import { selectEstablishmentType } from "../../redux/features/establishmentType/establishmentTypeSlice";
import { CheckboxQuestions, EstablishmentTypes, SubQuestion, SubQuestionTypes } from "../../types/QuestionTypes";
import { selectEstablishmentTypeQuestion } from "../../redux/features/establishmentTypeQuestion/establishmentTypeQuestionSlice";
import { FormWrapper, StyledButton } from "../../components/styles";
import { CollapseProps } from 'antd';
import DynamicOptionForm from "./DynamicOptionForm";
import { transformCheckboxData, formattedOptions, removeMatchingKeys, sortByTrueFirst } from "../../utils/helpers/dataTransformer";

type ModalProps = {
    isModalOpen: boolean;
    handleSave: (v: any) => void;
    onCancel: () => void;
    form: FormInstance<any>;
    hasAddPermission: boolean,
    hasEditPermission: boolean,
    hasViewPermission: boolean,
    selectedEstablishmentTypeId?: string | null;
};

const initialCheckboxQuestions: CheckboxQuestions[] = [{ id: 1, name: "true", subQuestion: [{ id: 1, fieldName: "", fieldType: 1, isRequired: false, }] }, { id: 1, name: "false", subQuestion: [{ id: 1, fieldName: "", fieldType: 1, isRequired: false, }] }];

const CreateEditModal: React.FC<ModalProps> = ({ isModalOpen, handleSave, onCancel, form, hasAddPermission, hasEditPermission, hasViewPermission, selectedEstablishmentTypeId }) => {
    const { loading } = useAppSelector(selectEstablishmentTypeQuestion);
    const { data: parentEstablishmentTypes, loading: parentEstablishmentTypesLoading } = useAppSelector(selectEstablishmentType);

    const [activeKey, setActiveKey] = useState<string | string[]>(["1"]);
    const [typeValue, setTypeValue] = useState<number | string>();

    const [questions, setQuestions] = useState<CheckboxQuestions[]>(initialCheckboxQuestions);
    const languageOptions = useMemo(
        () => [
            { code: "en", label: "English" },
            { code: "es", label: "Spanish" },
            { code: "fr", label: "French" },
            { code: "de", label: "German" },
            { code: "pt", label: "Portuguese" },
            { code: "ar", label: "Arabic" },
        ],
        []
    );

    const parentEstablishmentTypeOptions = useMemo(() => parentEstablishmentTypes?.map((c) => ({ label: c.name, value: c.id })) || [], [parentEstablishmentTypes]);

    const selectedEstablishmentType = Form.useWatch(["establishmentType"], form);
    const selectedQuestionType = Form.useWatch(["questionType"], form);

    // Get question types from ALL establishment types (cross-type support)
    const availableQuestionTypes = useMemo(() => {
        if (!selectedEstablishmentType) return [];

        // Collect all question types from ALL establishment types
        const allQuestionTypes: { label: string; value: string; establishmentType: string }[] = [];

        parentEstablishmentTypes.forEach(establishmentType => {
            if (establishmentType.specificTypes) {
                establishmentType.specificTypes
                    .filter(st => st.isQuestionType)
                    .forEach(st => {
                        allQuestionTypes.push({
                            label: `${st.name} (${establishmentType.name})`,
                            value: st.name,
                            establishmentType: establishmentType.name
                        });
                    });
            }
        });

        return allQuestionTypes;
    }, [selectedEstablishmentType, parentEstablishmentTypes]);
    const canSaveChanges = useMemo(() => hasAddPermission || hasEditPermission, [hasAddPermission, hasEditPermission]);

    // Auto-set establishment type when modal opens
    useEffect(() => {
        if (isModalOpen && selectedEstablishmentTypeId && !form.getFieldValue('id')) {
            // Only set for new questions, not when editing
            form.setFieldsValue({ establishmentType: selectedEstablishmentTypeId });
        }
    }, [isModalOpen, selectedEstablishmentTypeId, form]);

    const parseTranslations = (value: any) => {
        if (!value) return {};
        if (typeof value === "string") {
            try {
                return JSON.parse(value) || {};
            } catch {
                return {};
            }
        }
        return value;
    };

    // Populate options when editing
    useEffect(() => {
        if (form.getFieldValue('id') && isModalOpen) {
            setTypeValue(form.getFieldValue('fieldType'));
            const currentOptions = form.getFieldValue("options");
            const hasCheckboxField = form.getFieldValue('fieldType') === EstablishmentTypes.Checkbox;
            if (currentOptions && Array.isArray(currentOptions) && hasCheckboxField) {
                setQuestions(sortByTrueFirst(currentOptions));
            } else {
                setQuestions(initialCheckboxQuestions);
            }
            // Only update translations without overwriting other fields
            const currentValues = form.getFieldsValue();
            form.setFieldsValue({
                ...currentValues,
                fieldNameTranslations: parseTranslations(form.getFieldValue("fieldNameTranslations")),
                placeholderTranslations: parseTranslations(form.getFieldValue("placeholderTranslations")),
            });
        } else if (!form.getFieldValue('id')) {
            setTypeValue("");
            setQuestions(initialCheckboxQuestions);
            form.setFieldsValue({
                fieldNameTranslations: {},
                placeholderTranslations: {},
                translationKey: undefined,
                isLFNotified: false,
            });
        }
    }, [form.getFieldValue('id'), isModalOpen, form]);

    const onChange = (key: string | string[]) => {
        setActiveKey(key);
    };

    // Handle adding new conditional questions
    const handleAddCondition = (type: 'true' | 'false') => {
        setQuestions((prev) => {
            // Find the question for the current type ('true' or 'false')
            return prev.map((question) => {
                if (question.name === type) {
                    const newSubQuestion = {
                        id: (question.subQuestion.length ?? 0) + 1,
                        fieldName: '',
                        fieldType: 1,
                        isRequired: false,
                    };
                    return {
                        ...question,
                        subQuestion: [...question.subQuestion, newSubQuestion],
                    };
                }
                return question;
            });
        });
    };

    // Handle removing conditional questions
    const handleRemoveCondition = (type: 'true' | 'false', id: number | string) => {
        setQuestions((prev) => {
            return prev.map((question) => {
                if (question.name === type) {
                    return {
                        ...question,
                        subQuestion: question.subQuestion.filter((item) => item.id !== id),
                    };
                }
                return question;
            });
        });
    };

    // Handle input field change (fieldName, fieldType, isRequired)
    const handleChange = (
        type: 'true' | 'false',
        id: number | string,
        field: keyof SubQuestion,
        value: string | boolean | number
    ) => {
        setQuestions((prev) => {
            return prev.map((question) => {
                if (question.name === type) {
                    const updatedSubQuestions = question.subQuestion.map((item) =>
                        item.id === id ? { ...item, [field]: value } : item
                    );
                    return {
                        ...question,
                        subQuestion: updatedSubQuestions,
                    };
                }
                return question;
            });
        });
    };

    useEffect(() => {
        form.setFieldValue("options", transformCheckboxData(questions));
    }, [form, questions]);

    const items: CollapseProps['items'] = [
        {
            key: '1',
            label: <>{activeKey && Array.isArray(activeKey) && activeKey.length > 0 ? <> <MinusOutlined /> Remove Conditional Question </> : <> <PlusOutlined /> Add Conditional Question </>}</>,
            children: <Form.Item name="options">
                {/* Conditional Sections */}
                {questions?.map((type) => {
                    return (
                        <div key={type.name} style={{ padding: "16px 0px", borderTop: `${type.name === "false" && `1px dashed #154075`}` }}>
                            <StyledTypography>{type.name === "true" ? "if True" : "if False"}</StyledTypography>
                            {type?.subQuestion?.map((q, index) => {
                                return (
                                    <StyledConditionalContainer key={q.id} className="conditional-item" style={{ marginBottom: "10px" }}>
                                        <AddFieldWrapper>
                                            <div>#{index + 1}</div>
                                            <StyledConditionalRemoveButton onClick={() => handleRemoveCondition(type.name, q.id)}>
                                                <MinusOutlined /> Remove
                                            </StyledConditionalRemoveButton>
                                        </AddFieldWrapper>
                                        <Form.Item
                                            label="Name"
                                            name={[type.name, q.id, "fieldName"]}
                                            initialValue={q.fieldName}
                                            rules={[{ required: true, message: "Field name is required" }]}
                                            style={{ width: "100%" }}
                                        >
                                            <Input
                                                size="large"
                                                placeholder="Field name"
                                                value={q.fieldName}
                                                onChange={(e) => handleChange(type.name, q.id, "fieldName", e.target.value)}
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            label="Type"
                                            name={[type.name, q.id, "fieldType"]}
                                            initialValue={q.fieldType}
                                            rules={[{ required: true, message: "Field type is required" }]}
                                            style={{ width: "100%" }}
                                        >
                                            <Select
                                                size="large"
                                                options={Object.entries(SubQuestionTypes).map(([key, value]) => ({
                                                    label: key,
                                                    value: value,
                                                }))}
                                                placeholder="Select field type"
                                                onChange={(value) => {
                                                    handleChange(type.name, q.id, "fieldType", value)
                                                }}
                                            />
                                        </Form.Item>

                                        <Form.Item style={{ width: "100%" }} name={[type.name, q.id, "isRequired"]} valuePropName="checked" initialValue={q.isRequired}>
                                            <Checkbox
                                                checked={q.isRequired}
                                                onChange={(e) =>
                                                    handleChange(type.name, q.id, "isRequired", e.target.checked)
                                                }
                                            >
                                                Is Required
                                            </Checkbox>
                                        </Form.Item>
                                    </StyledConditionalContainer>
                                )
                            })}

                            <AddAnotherConditionalStyledButton onClick={() => handleAddCondition(type.name)}>
                                <PlusOutlined /> Add Another Conditional Question
                            </AddAnotherConditionalStyledButton>
                        </div>
                    )
                })}
            </Form.Item>,
        },
    ];

    return (
        <StyledModal
            draggable={true}
            width={650}
            title={form.getFieldValue('id') ? `${canSaveChanges ? "Edit" : "View"} Establishment Type Question` : `${canSaveChanges ? "Add" : "View"} Establishment Type Question`}
            open={isModalOpen}
            closeIcon={null}
            footer={
                <FooterButtons>
                    <StyledButton onClick={() => {
                        onCancel();
                        setQuestions(initialCheckboxQuestions);
                    }} disabled={loading}>
                        Cancel
                    </StyledButton>
                    {canSaveChanges && <StyledButton
                        type="primary"
                        loading={loading}
                        onClick={async () => {
                            try {
                                const values = await form.validateFields();
                                const cleanedValues = removeMatchingKeys(values);

                                // If questionType is set (dynamic establishment list), don't require options
                                let formattedOption;
                                if (cleanedValues?.questionType) {
                                    // Dynamic options from establishments - set empty array
                                    formattedOption = [];
                                } else if (cleanedValues?.fieldType === EstablishmentTypes.Option) {
                                    // Static options - format them
                                    formattedOption = formattedOptions(cleanedValues?.options);
                                } else {
                                    formattedOption = cleanedValues?.options;
                                }

                                handleSave({
                                    ...cleanedValues,
                                    options: formattedOption,
                                    isRequired: !!values.isRequired,
                                    isLFNotified: !!values.isLFNotified,
                                });
                            } catch (error) {
                                console.error("Validation failed:", error);
                            }
                        }}
                    >
                        Save
                    </StyledButton>}
                </FooterButtons>
            }
            centered
            onCancel={() => !loading && onCancel()}

        >
            <FormWrapper form={form} layout="vertical" disabled={loading || !canSaveChanges}>
                <Form.Item label="Parent Establishment Type" name="establishmentType" rules={[{ required: true, message: "Please select a parent establishmentType" }]}>
                    <Select
                        size="large"
                        placeholder="Select Establishment Type"
                        options={parentEstablishmentTypeOptions}
                        loading={parentEstablishmentTypesLoading}
                        disabled={!!selectedEstablishmentTypeId || !!form.getFieldValue('id')}
                    />
                </Form.Item>
                {selectedEstablishmentType && availableQuestionTypes.length > 0 && (
                    <Form.Item
                        label="Question Type"
                        name="questionType"
                        tooltip="Select a specific type to associate this question with. This question will apply to all establishments of the selected type. Only specific types marked as 'Question Type' are shown."
                    >
                        <Select
                            size="large"
                            placeholder="Select Question Type (optional)"
                            options={availableQuestionTypes}
                            allowClear
                        />
                    </Form.Item>
                )}
                {selectedEstablishmentType && (
                    <>
                        <Form.Item label="Name" name="fieldName" rules={[{ required: true, message: "Field name is required" }]}>
                            <Input size="large" placeholder="Field name" />
                        </Form.Item>
                        <Form.Item label="Translation Key" name="translationKey" tooltip="Optional i18n key for this question">
                            <Input size="large" placeholder="e.g., landing.questions.location" />
                        </Form.Item>
                        <div style={{ marginBottom: 8, fontWeight: 600 }}>Localized Field Names</div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
                            {languageOptions.map((lang) => (
                                <Form.Item key={lang.code} name={["fieldNameTranslations", lang.code]} label={lang.label}>
                                    <Input size="large" placeholder={`Label in ${lang.label}`} />
                                </Form.Item>
                            ))}
                        </div>
                        <div style={{ marginBottom: 8, fontWeight: 600 }}>Localized Placeholders</div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
                            {languageOptions.map((lang) => (
                                <Form.Item key={lang.code} name={["placeholderTranslations", lang.code]} label={lang.label}>
                                    <Input size="large" placeholder={`Placeholder in ${lang.label}`} />
                                </Form.Item>
                            ))}
                        </div>
                        <Form.Item label="Type" name="fieldType" rules={[{ required: true, message: "Field type is required" }]}>
                            <Select
                                size="large"
                                options={Object.entries(EstablishmentTypes).map(([key, value]) => ({
                                    label: key,
                                    value: value,
                                }))}
                                placeholder="Select field type"
                                onChange={(value) => {
                                    setTypeValue(value);
                                }}
                            />
                        </Form.Item>

                        {typeValue === EstablishmentTypes.Option && !selectedQuestionType && <DynamicOptionForm form={form} />}

                        {typeValue === EstablishmentTypes.Option && selectedQuestionType && (
                            <div style={{
                                padding: '12px 16px',
                                backgroundColor: '#e6f4ff',
                                border: '1px solid #91caff',
                                borderRadius: '6px',
                                marginBottom: '16px'
                            }}>
                                <p style={{ margin: 0, color: '#0958d9', fontSize: '14px' }}>
                                    ℹ️ <strong>Dynamic Options Enabled</strong>
                                </p>
                                <p style={{ margin: '4px 0 0 0', color: '#595959', fontSize: '13px' }}>
                                    Options will be automatically populated from establishments with specific type: <strong>{selectedQuestionType}</strong>
                                </p>
                            </div>
                        )}

                        <Form.Item name="isRequired" valuePropName="checked">
                            <Checkbox>Is Required</Checkbox>
                        </Form.Item>
                        <Form.Item
                            name="isLFNotified"
                            valuePropName="checked"
                            tooltip="Mark this question as Lost & Found related. Locations selected in this question will be prioritized in the LF Notification modal."
                        >
                            <Checkbox>Mark as L&F Notification Question</Checkbox>
                        </Form.Item>
                        {typeValue === EstablishmentTypes.Checkbox && <StyledCollapse expandIcon={() => null} items={items} activeKey={activeKey} onChange={onChange} />}
                    </>
                )}
            </FormWrapper>

        </StyledModal>
    );
};

export default CreateEditModal;
